/**
 * Job list presets — predefined filter sets for the admin Jobs console.
 *
 * SINGLE SOURCE OF TRUTH. This registry drives three surfaces that must never
 * drift apart:
 *   1. the sidebar submenu under "Jobs" (App.vue)
 *   2. the on-page preset bar (Jobs.vue)
 *   3. the live count + revenue badges on each of the above
 *      (POST /api/admin/jobs/counts)
 *
 * Each preset's `build()` returns a canonical filter descriptor:
 *   { status: string[], search: { and: Condition[] } }
 * `status`   -> job status strings (sent to the API `status` param / the
 *               `filterStatus` multiselect).
 * `search`   -> the advanced-search object serialized into `search_json`
 *               (the `CREATED_FROM` floor, sub-status "state" tokens,
 *               appointment-date bounds, ...). Never null — every preset
 *               carries at least the created-date floor.
 *
 * `build()` is a function (not a static object) so time-relative presets such as
 * "outdated appointment" can compute `now` at apply/count time.
 *
 * The list is expected to grow — add a preset object + an i18n title
 * (`job-presets.titles.<id>`) and it appears everywhere automatically.
 */
import { JobStatusEnum } from '@gid/models';
import { StatusRulesNamesEnum } from '@gid/models/dist/entities/status-rules-names-enum';

// Lifecycle-ordered groups. Each id has an i18n label `job-presets.groups.<id>`.
export const JOB_PRESET_GROUPS = [
  'creation',
  'before-quote',
  'quote-approval',
  'execution',
  'documentation',
  'invoicing',
];

const OHBA = JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED;

// Sub-status ("state") tokens for `on hold before accepted`. The token format is
// `<status>.<status-rule-name>.<condition>` and is matched as an opaque element
// of the job_state.statuses text[] array by the backend — these must stay in
// sync with the "state" advanced-filter values in Jobs.vue.
const OHBA_STATE = {
  adminValidation: `${OHBA}.${StatusRulesNamesEnum.ADMIN_VALIDATION}.pending`,
  partnerQuoteValidation: `${OHBA}.${StatusRulesNamesEnum.PARTNER_QUOTE_VALIDATION}.pending-validation`,
  networkOperator: `${OHBA}.${StatusRulesNamesEnum.NETWORK_OPERATOR}.waiting`,
  agreementOwner: `${OHBA}.${StatusRulesNamesEnum.AGREEMENT_OWNER}.waiting`,
  customerPrework: `${OHBA}.${StatusRulesNamesEnum.CUSTOMER_PREWORK_BEFORE_ACCEPTED}.waiting`,
};

// Every preset is scoped to jobs created from this instant onwards. The preset
// views are work queues and their count / revenue badges are read as current
// volume, so pre-2025 leftovers distort both. UTC because `job.createddate` is
// stored in UTC — a local-midnight bound would make the same preset report
// different numbers for operators in different timezones.
const CREATED_FROM = '2025-01-01T00:00:00.000Z';

const stateCond = (op, tokens) => ({ op, field: 'state', value: tokens });

// The date floor lives in `build()` rather than in the count endpoint so it
// reaches BOTH surfaces from one place: the list filters (presetToStoreFilter)
// and the count + revenue payloads (presetCountRequests). Filtering only the
// counts would make a badge contradict the rows it sits above.
const createdFromCond = () => ({
  op: 'includes',
  field: 'createddate',
  // backend parses this as JSON and reads any key containing `_from`
  value: JSON.stringify({ createddate_from: CREATED_FROM }),
});

const search = (...conds) => ({ and: [createdFromCond(), ...conds] });

// A single-status preset (the common case).
const status =
  (...statuses) =>
  () => ({ status: statuses, search: search() });

export const JOB_PRESETS = [
  // -- creation ---------------------------------------------------------------
  { id: 'new', group: 'creation', build: status(JobStatusEnum.NEW) },
  {
    id: 'waiting-onboarding',
    group: 'creation',
    build: status(JobStatusEnum.WAITING_FOR_ONBOARDING),
  },

  // -- before quote sent (on hold before accepted, by sub-status) ------------
  {
    id: 'quote-admin-validation',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(stateCond('includes', [OHBA_STATE.adminValidation])),
    }),
  },
  {
    id: 'quote-partner-validation',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(
        stateCond('includes', [OHBA_STATE.partnerQuoteValidation]),
      ),
    }),
  },
  {
    id: 'quote-network-operator',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(stateCond('includes', [OHBA_STATE.networkOperator])),
    }),
  },
  {
    id: 'quote-agreement-owner',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(stateCond('includes', [OHBA_STATE.agreementOwner])),
    }),
  },
  {
    id: 'quote-customer-prework',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(stateCond('includes', [OHBA_STATE.customerPrework])),
    }),
  },
  {
    // Everything else in `on hold before accepted` — jobs whose state carries
    // none of the specifically-handled sub-statuses above (incl. jobs with no
    // state token at all).
    id: 'quote-generic',
    group: 'before-quote',
    build: () => ({
      status: [OHBA],
      search: search(stateCond('excludes', Object.values(OHBA_STATE))),
    }),
  },

  // -- quote approval ---------------------------------------------------------
  {
    id: 'proposal-approval',
    group: 'quote-approval',
    build: status(JobStatusEnum.PROPOSAL_APPROVAL_PENDING),
  },
  {
    id: 'brand-proposal-approval',
    group: 'quote-approval',
    build: status(JobStatusEnum.BRAND_PROPOSAL_APPROVAL_PENDING),
  },

  // -- scheduling & execution -------------------------------------------------
  {
    id: 'open-no-partner',
    group: 'execution',
    build: status(JobStatusEnum.OPEN),
  },
  {
    id: 'accepted-no-appointment',
    group: 'execution',
    build: status(JobStatusEnum.ACCEPTED),
  },
  { id: 'on-hold', group: 'execution', build: status(JobStatusEnum.ON_HOLD) },
  {
    // `appointment confirmed` whose appointment start is already in the past.
    id: 'outdated-appointment',
    group: 'execution',
    build: () => ({
      status: [JobStatusEnum.APPOINTMENT_CONFIRMED],
      search: search({
        op: 'includes',
        field: 'appointment_date',
        // backend parses this as JSON and reads any key containing `_to`
        value: JSON.stringify({
          appointment_date_to: new Date().toISOString(),
        }),
      }),
    }),
  },
  { id: 'started', group: 'execution', build: status(JobStatusEnum.STARTED) },

  // -- documentation ----------------------------------------------------------
  {
    id: 'approval-pending',
    group: 'documentation',
    build: status(JobStatusEnum.APPROVAL_PENDING),
  },
  {
    id: 'on-hold-after-protocol',
    group: 'documentation',
    build: status(JobStatusEnum.ON_HOLD_AFTER_PROTOCOL_APPROVAL),
  },

  // -- invoicing --------------------------------------------------------------
  {
    id: 'completed',
    group: 'invoicing',
    build: status(JobStatusEnum.COMPLETED),
  },
  {
    id: 'invoice-sent',
    group: 'invoicing',
    build: status(JobStatusEnum.INVOICE_SENT),
  },
];

export function getPreset(id) {
  return JOB_PRESETS.find((p) => p.id === id) || null;
}

export function presetsByGroup() {
  return JOB_PRESET_GROUPS.map((group) => ({
    group,
    presets: JOB_PRESETS.filter((p) => p.group === group),
  })).filter((g) => g.presets.length);
}

/**
 * Payloads for the batch counts endpoint — one entry per preset, in the same
 * shape the list endpoint filters on.
 */
export function presetCountRequests() {
  return JOB_PRESETS.map((preset) => {
    const f = preset.build();
    return {
      id: preset.id,
      status: f.status && f.status.length ? f.status : null,
      search_json: f.search || null,
    };
  });
}

/**
 * SET_JOB_FILTER payload that reproduces a preset in the Jobs list.
 * @param {object} preset      a JOB_PRESETS entry
 * @param {function} [statusText] optional (statusValue) => localized label,
 *                                used only for the multiselect chip display.
 */
export function presetToStoreFilter(preset, statusText) {
  const f = preset.build();
  const statuses = (f.status || []).map((value) => ({
    value,
    text: statusText ? statusText(value) : value,
  }));
  return {
    search: '',
    owner: null,
    status: statuses,
    useAdvancedSearch: !!f.search,
    advancedSearch: f.search || null,
    useQuickSearch: false,
    quickSearch: null,
  };
}
